import WatsonApi from "../../backends/WatsonApi";
import {SetStateAction, useContext, useState} from "react";
import {StoreContext} from "../../stores/StoreLoader";
import NotificationManager from "../../components/notifications/NotificationManager";
import {NotificationItem} from "./NotificationItem";
import styles from "../styles/PushSubscriptionItem.module.scss";
import {IPersonBlockWithPushSubscriptions, settingsView} from "./UserSettingsPage";
import ToggleSwitch from "../../components/admin/sidebar/items/ToggleSwitch";
import {formatPhoneNumber} from "../../components/modals/PersonModal/TeacherBio";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import {observer} from "mobx-react";

const {UAParser} = require("ua-parser-js");

export function askPermission() {
    return new Promise(function (resolve, reject) {
        const permissionResult = Notification.requestPermission(function (result) {
            resolve(result);
        });

        if (permissionResult) {
            permissionResult.then(resolve, reject);
        }
    }).then(function (permissionResult) {
        if (permissionResult !== 'granted') {
            throw new Error("Permission not granted to enable push notifications! Please try again.");
        }
    });
}

export function isPushSupported() {
    return ('PushManager' in window && 'Notification' in window);
}

export function getDeviceStringFromUserAgent(userAgent) {
    const parsed = new UAParser(userAgent).getResult();
    let deviceModel = parsed.device.model;
    if (!deviceModel) {
        // ua-parser doesn't handle desktop devices, this is a best guess
        // see https://github.com/faisalman/ua-parser-js/issues/182
        deviceModel = `${parsed.os.name} Desktop`;
    }
    return `${deviceModel || "Unknown Device"}/${parsed.browser.name}`
}

export const NotificationSettings = observer((props: {
    notificationSettings: any,
    setNotificationSettings: any,
    userObj: IPersonBlockWithPushSubscriptions,
    setCurrentView: SetStateAction<any>,
}) => {
    const {organizationStore, interfaceStore, userStore} = useContext(StoreContext);

    const emailEnabled = !!(props.userObj.blockModel.notifications?.email && props.userObj.blockModel.email);
    const phoneEnabled = !!(props.userObj.blockModel.notifications?.phone && props.userObj.blockModel.mobile_phone);
    const pushEnabled = !!props.userObj.blockModel.notifications?.push;
    const pushSubscriptions = props.userObj.blockModel.push_subscriptions;

    useAsyncEffect(async () => {
        if (pushSubscriptions.length === 0 && pushEnabled) {
            // effectively turning off push notifications if there are no subscriptions
            await updateNotificationSetting("push");
        } else if (!pushEnabled && pushSubscriptions.length > 0) {
            // turn on push notifications there are any active subscriptions
            await updateNotificationSetting("push");
        }
    }, [pushSubscriptions.length, pushEnabled])

    const updateNotificationSetting = async (field: "push" | "phone" | "email") => {
        try {
            const stateMapping = {
                phone: phoneEnabled,
                push: pushEnabled,
                email: emailEnabled,
            }

            const client = await WatsonApi();
            const response = await client.apis.organizations.organizations_users_partial_update({
                organization_pk: organizationStore.currentOrganization.id,
                id: props.userObj.blockModel.id,
                data: {
                    notifications: {
                        phone: phoneEnabled,
                        email: emailEnabled,
                        push: pushEnabled,
                        [field]: !stateMapping[field],
                    }
                }
            })
            const userObj = JSON.parse(response.data);
            props.userObj.setAttributes({
                blockModel: {
                    ...props.userObj.blockModel,
                    notifications: userObj.notifications,
                }
            })
            NotificationManager.success("Settings have been updated!");
        } catch (e) {
            throw new Error("Something went wrong! Please try again.")
        }
    }

    async function createPushSubscription() {
        try {
            const registration = await navigator.serviceWorker.ready;
            await askPermission();
            const pushSubscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: "BFcZZ6FiITumPysCi_t9B1TANBHATH7r0Y4bKVRomTVRo14k5eNU0L9LhgZ3ogL7xPkGYh-RqtFN14RlnU3rDWg",
            });
            console.log("Received PushSubscription: ", JSON.stringify(pushSubscription));
            const client = await WatsonApi();
            const push_subscription_response = await client.apis.users.users_push_subscription_create({
                user_pk: userStore.id,
                data: {
                    organization_id: organizationStore.organization.id,
                    user_id: userStore.id,
                    subscription_object: pushSubscription,
                    user_agent: interfaceStore.userAgent,
                },
            })
            const newSubscription = JSON.parse(push_subscription_response.data);
            props.userObj.setAttributes({
                blockModel: {
                    ...props.userObj.blockModel,
                    push_subscriptions: [...props.userObj.blockModel.push_subscriptions.filter(sub => {
                        // in case we upserted the new subscription, don't show a duplicate
                        return sub.id !== newSubscription.id
                    }), {
                        ...newSubscription,
                        organization: organizationStore.organization,
                    }],
                }
            })
        } catch (subscriptionError) {
            throw new Error(subscriptionError.message);
        }
    }

    async function deletePushSubscription(push_subscription) {
        const client = await WatsonApi();
        await client.apis.users.users_push_subscription_delete({
            user_pk: userStore.id,
            id: (push_subscription as PushSubscription).id,
        });
        props.userObj.setAttributes({
            blockModel: {
                ...props.userObj.blockModel,
                push_subscriptions: props.userObj.blockModel.push_subscriptions.filter(sub => {
                    return sub.id !== (push_subscription as PushSubscription).id
                }),
            }
        })
    }

    const subscriptionForCurrentDeviceAndOrganization = pushSubscriptions.find(sub => {
        return getDeviceStringFromUserAgent(sub.user_agent) === getDeviceStringFromUserAgent(interfaceStore.userAgent) &&
            sub.organization_id === organizationStore.organization.id;
    })

    return <div id="my-notification-settings-tab">
        <div className={`sb-settings-block-content-heading`}>
            <h4>Notification Settings</h4>
            <br/>
            <div className="row">
                <div className="col-sm-12">
                    <p>
                        Notifications will be sent to the most appropriate device. Emergency notifications will be sent
                        to all devices.
                    </p>
                    <div className={styles.container}>
                        <div className={styles.itemContainer}>
                            <div>
                                {props.userObj.blockModel.mobile_phone ?
                                    <span>{formatPhoneNumber(props.userObj.blockModel.mobile_phone)}</span> :
                                    <button onClick={() => props.setCurrentView(settingsView.myPublicInfo)}>Add a
                                        Phone Number</button>}
                                <small>Text Messages</small>
                            </div>
                            <div>
                                <ToggleSwitch
                                    onClick={() => updateNotificationSetting("phone")}
                                    on={phoneEnabled}
                                    enabled={!!props.userObj.blockModel.mobile_phone}
                                    title={props.userObj.blockModel.mobile_phone ? "Toggle text messages" : "Add a phone number to enable"}
                                />
                            </div>
                        </div>
                        <div className={styles.itemContainer}>
                            <div>
                                {props.userObj.blockModel.email ? <span>{props.userObj.blockModel.email}</span> :
                                    <button onClick={() => props.setCurrentView(settingsView.myPublicInfo)}>Add an
                                        Email</button>}
                                <small>Email</small>
                            </div>
                            <div>
                                <ToggleSwitch
                                    onClick={() => updateNotificationSetting("email")}
                                    on={emailEnabled}
                                    enabled={!!props.userObj.blockModel.email}
                                    title={props.userObj.blockModel.email ? "Toggle text messages" : "Add an email to enable"}
                                />
                            </div>
                        </div>
                        {pushSubscriptions
                            .filter(sub => {
                                return subscriptionForCurrentDeviceAndOrganization ?
                                    sub.id !== subscriptionForCurrentDeviceAndOrganization.id
                                    : true;
                            })
                            .map(sub => {
                                return <NotificationItem
                                    key={sub.id}
                                    push_subscription={sub}
                                    deletePushSubscription={deletePushSubscription}
                                />
                            })}
                        <div className={styles.itemContainer}>
                            <div>
                                <span>{organizationStore.organization.title}</span>
                                <span>{getDeviceStringFromUserAgent(interfaceStore.userAgent)}</span>
                                <small>App Notifications</small>
                            </div>
                            <div>
                                <ToggleSwitch
                                    enabled={isPushSupported()}
                                    title={isPushSupported() ? "Enable app notifications on current device" : "Push notifications are not supported on this device or browser"}
                                    onClick={async () => {
                                        if (subscriptionForCurrentDeviceAndOrganization) {
                                            await deletePushSubscription(subscriptionForCurrentDeviceAndOrganization);
                                        } else {
                                            try {
                                                await createPushSubscription();
                                            } catch (e) {
                                                NotificationManager.error(e.message);
                                            }
                                        }
                                    }}
                                    on={subscriptionForCurrentDeviceAndOrganization}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
}
)
